import { useCallback, useEffect, useSyncExternalStore } from "react";

import { getMarkerWidget, subscribeToMarkerWidget } from "setup/marker-io";

import IntercomSingleton, {
  INTERCOM_LAUNCHER_ABOVE_HELP_PILL_PX,
  INTERCOM_LAUNCHER_CORNER_PADDING_PX
} from "utils/intercom.util";

/**
 * Two vendor buttons float over the bottom-left corner: the Intercom launcher and
 * the Marker.io "Help" pill, stacked, which together cover the side menu's last
 * rows (ENG-2535 follow-up). While the redesigned menu is mounted, this hook puts
 * Help in the footer as a row of its own and drops the launcher to the corner, so
 * the footer needs room for one button rather than two. On unmount (the classic
 * rail, the login page) both vendor buttons go back where they were.
 */
export default function useSupportWidgetsInFooter() {
  const marker = useSyncExternalStore(
    subscribeToMarkerWidget,
    getMarkerWidget,
    getMarkerWidget
  );

  useEffect(() => {
    if (!marker) return undefined;
    marker.hide();
    return () => marker.show();
  }, [marker]);

  useEffect(() => {
    const intercom = IntercomSingleton.getInstance();
    intercom.setLauncherVerticalPadding(INTERCOM_LAUNCHER_CORNER_PADDING_PX);
    return () => {
      intercom.setLauncherVerticalPadding(INTERCOM_LAUNCHER_ABOVE_HELP_PILL_PX);
    };
  }, []);

  // Marker.io's documented "use your own button" call: capture the page, then its
  // feedback form, exactly what its own pill does.
  const openHelp = useCallback(() => {
    void marker?.capture("fullscreen");
  }, [marker]);

  return { helpAvailable: marker !== undefined, openHelp };
}
